import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { X, GitCompareArrows } from "lucide-react";
import { useCompare } from "@/hooks/useCompare";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { sortImages, type ProductWithImagesRow } from "@/lib/productUtils";
import { imgThumb } from "@/lib/imageUrl";

export function CompareBar() {
  const { ids, clear, toggle, count } = useCompare();
  const navigate = useNavigate();

  const { data: products } = useQuery({
    queryKey: ["compare-bar-products", ids.join(",")],
    enabled: ids.length > 0,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const { data } = await supabase
        .from("products")
        .select("id, name, product_images(image_url, sort_order)")
        .in("id", ids);
      return (data as ProductWithImagesRow[] ?? []).map((p) => {
        const imgs = sortImages(p.product_images);
        return { id: p.id, name: p.name, imageUrl: imgs[0]?.image_url ?? "" };
      });
    },
  });

  if (count === 0) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-border shadow-2xl py-3 px-4 md:px-6 animate-in slide-in-from-bottom-4 duration-300">
      <div className="container flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-1.5 text-sm font-medium text-[#1A3870] shrink-0">
          <GitCompareArrows className="h-4 w-4 text-accent" aria-hidden="true" />
          Comparar ({count}/4):
        </div>

        <div className="flex gap-2 flex-1 overflow-x-auto min-w-0">
          {products?.map((p) => (
            <div key={p.id} className="flex items-center gap-1.5 bg-muted rounded-full pl-1 pr-2 py-0.5 shrink-0">
              {p.imageUrl && (
                <img src={imgThumb(p.imageUrl)} alt={p.name} width={24} height={24} loading="lazy" decoding="async" className="h-6 w-6 rounded-full object-cover" />
              )}
              <span className="text-xs font-medium max-w-[100px] truncate">{p.name}</span>
              <button
                onClick={() => toggle(p.id)}
                className="min-h-[44px] min-w-[44px] flex items-center justify-center text-muted-foreground hover:text-destructive transition-colors shrink-0 -mr-1"
                aria-label={`Remover ${p.name} da comparação`}
              >
                <X className="h-3 w-3" aria-hidden="true" />
              </button>
            </div>
          ))}

          {/* Empty slots */}
          {Array.from({ length: 4 - count }).map((_, i) => (
            <div key={i} className="flex items-center justify-center h-8 w-24 border border-dashed border-border rounded-full">
              <span className="text-xs text-muted-foreground">+</span>
            </div>
          ))}
        </div>

        <div className="flex gap-2 shrink-0">
          <Button
            size="sm"
            variant="outline"
            onClick={clear}
            className="h-8 text-xs"
          >
            Limpar
          </Button>
          <Button
            size="sm"
            disabled={count < 2}
            onClick={() => navigate(`/comparar?ids=${ids.join(",")}`)}
            className="h-8 text-xs bg-accent text-accent-foreground hover:bg-accent/90 gap-1.5"
          >
            <GitCompareArrows className="h-3.5 w-3.5" aria-hidden="true" />
            Comparar agora
          </Button>
        </div>
      </div>
    </div>
  );
}
